import { useEffect, useRef, useState } from "react";
import { Camera, CameraOff, Check, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { processImage, processBlob } from "../../lib/imageProcess";
import { enqueueLocal, syncEntry } from "../../lib/photoQueue";
import { API_BASE } from "../../lib/api";

/**
 * PhotoCapture -- Kamera-Dialog für Mobile-Flows.
 *
 * Props:
 *   entityType (required)
 *   purpose (required)
 *   entityId (optional, kann später gesetzt werden)
 *   isEvidence (default false)
 *   onCaptured(photoId, queueEntryId) -- fires when sync OR enqueue success
 *   triggerLabel -- Button-Label (default "Foto aufnehmen")
 *   requireSync -- wenn true, wartet bis sync erfolgreich bevor onCaptured feuert
 *                  (für Pflicht-Flows). Sonst reicht lokale Queue.
 *   multiple -- default false; true = dialog bleibt offen, mehrere Fotos
 *   defaultOpen -- bei true direkt öffnen (externally controlled)
 */
export function PhotoCapture({
    entityType,
    purpose,
    entityId = null,
    isEvidence = false,
    onCaptured,
    triggerLabel = "Foto aufnehmen",
    requireSync = false,
    multiple = false,
    renderTrigger,
    testId = "photo-capture",
}) {
    const [open, setOpen] = useState(false);
    const [stream, setStream] = useState(null);
    const [cameraError, setCameraError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [preview, setPreview] = useState(null); // {url, processed}
    const [uploading, setUploading] = useState(false);
    const [shotCount, setShotCount] = useState(0);
    const videoRef = useRef(null);
    // Spiegel des Streams für den Unmount-Fall: wird die Komponente mit
    // offenem Dialog entfernt (Navigation), muss die Kamera trotzdem aus.
    const streamRef = useRef(null);
    // R13: verspätete Upload-Callbacks nicht mehr feuern, wenn die
    // Komponente weg ist; Parent unterscheidet zusätzlich per isLate.
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!open) return;
        setCameraError(null);
        if (!navigator.mediaDevices?.getUserMedia) {
            setCameraError("no-api");
            return;
        }
        let active = true;
        navigator.mediaDevices
            .getUserMedia({
                video: { facingMode: { ideal: "environment" } },
                audio: false,
            })
            .then((s) => {
                if (!active || !mountedRef.current) {
                    s.getTracks().forEach((t) => t.stop());
                    return;
                }
                streamRef.current = s;
                setStream(s);
                if (videoRef.current) {
                    videoRef.current.srcObject = s;
                }
            })
            .catch((e) => {
                setCameraError(e.name || "no-permission");
            });
        return () => {
            active = false;
        };
    }, [open]);

    useEffect(() => {
        if (!open && stream) {
            stream.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
            setStream(null);
        }
    }, [open, stream]);

    const reset = () => {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview(null);
    };

    const handleClose = () => {
        reset();
        setOpen(false);
        setShotCount(0);
    };

    const shoot = async () => {
        if (!videoRef.current || !stream) return;
        setProcessing(true);
        try {
            const v = videoRef.current;
            const canvas = document.createElement("canvas");
            canvas.width = v.videoWidth || 1280;
            canvas.height = v.videoHeight || 720;
            canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
            const blob = await new Promise((r) =>
                canvas.toBlob((b) => r(b), "image/jpeg", 0.9)
            );
            const processed = await processBlob(blob);
            const url = URL.createObjectURL(processed.blob);
            setPreview({ url, processed });
        } catch (e) {
            toast.error(`Foto-Fehler: ${e.message}`);
        } finally {
            setProcessing(false);
        }
    };

    const handleFilePick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setProcessing(true);
        try {
            const processed = await processImage(file);
            const url = URL.createObjectURL(processed.blob);
            setPreview({ url, processed });
        } catch (err) {
            toast.error(`Fehler: ${err.message}`);
        } finally {
            setProcessing(false);
        }
    };

    const commit = async () => {
        if (!preview) return;
        setUploading(true);
        try {
            const entry = await enqueueLocal({
                entityType,
                entityId,
                purpose,
                isEvidence,
                blob: preview.processed.blob,
                thumb: preview.processed.thumb,
                width: preview.processed.width,
                height: preview.processed.height,
                mime: preview.processed.mime,
                ext: preview.processed.ext,
                takenAt: new Date().toISOString(),
            });

            if (requireSync) {
                try {
                    const resp = await syncEntry(entry, API_BASE);
                    onCaptured?.(resp.id, entry.id);
                    toast.success("Foto gespeichert.");
                } catch (e) {
                    toast.error(`Upload fehlgeschlagen: ${e.message}`);
                    setUploading(false);
                    return;
                }
            } else {
                onCaptured?.(null, entry.id);
                toast.success("Foto in Warteschlange.");
                // fire background sync -- R13: als später Callback markiert
                // und nur solange die Komponente lebt; der Parent entscheidet
                // per isLate, ob seine Capture-Session noch dieselbe ist.
                syncEntry(entry, API_BASE)
                    .then((resp) => {
                        if (!mountedRef.current) return;
                        onCaptured?.(resp.id, entry.id, true);
                    })
                    .catch(() => {});
            }

            setShotCount((n) => n + 1);
            reset();
            if (!multiple) handleClose();
        } finally {
            setUploading(false);
        }
    };

    const defaultTrigger = (
        <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(true)}
            data-testid={`${testId}-trigger`}
        >
            <Camera className="mr-2 h-4 w-4" />
            {triggerLabel}
        </Button>
    );

    return (
        <>
            {renderTrigger ? (
                <span onClick={() => setOpen(true)} data-testid={`${testId}-trigger`}>
                    {renderTrigger()}
                </span>
            ) : (
                defaultTrigger
            )}
            <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleClose())}>
                <DialogContent
                    className="max-w-lg"
                    data-testid={`${testId}-dialog`}
                >
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Foto aufnehmen</h2>
                        {multiple && shotCount > 0 ? (
                            <span className="text-xs text-muted-foreground">
                                {shotCount} gespeichert
                            </span>
                        ) : null}
                    </div>

                    {preview ? (
                        <div className="flex flex-col gap-3">
                            <img
                                src={preview.url}
                                alt="Preview"
                                className="max-h-80 w-full rounded-md object-contain"
                                data-testid={`${testId}-preview`}
                            />
                            <div className="flex items-center justify-between gap-2">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={reset}
                                    data-testid={`${testId}-discard`}
                                >
                                    <Trash2 className="mr-1 h-4 w-4" /> Verwerfen
                                </Button>
                                <Button
                                    type="button"
                                    onClick={commit}
                                    disabled={uploading}
                                    data-testid={`${testId}-commit`}
                                >
                                    {uploading ? (
                                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Check className="mr-1 h-4 w-4" />
                                    )}
                                    Übernehmen
                                </Button>
                            </div>
                        </div>
                    ) : cameraError ? (
                        // #118: Vorschau vor dem Kamerafehler prüfen, sonst ist
                        // der Datei-Fallback ohne Kamera wirkungslos.
                        <div className="flex flex-col items-center gap-3 rounded-md border border-border bg-card/40 p-4">
                            <CameraOff className="h-10 w-10 text-muted-foreground" />
                            <div className="text-center text-sm text-muted-foreground">
                                Kamera nicht verfügbar. Datei-Upload nutzen.
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFilePick}
                                className="text-xs"
                                data-testid={`${testId}-file-fallback`}
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            <div className="relative overflow-hidden rounded-md border border-border bg-black">
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    className="h-72 w-full object-contain"
                                    data-testid={`${testId}-video`}
                                />
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={handleClose}
                                    data-testid={`${testId}-cancel`}
                                >
                                    <X className="mr-1 h-4 w-4" /> Abbrechen
                                </Button>
                                <Button
                                    type="button"
                                    onClick={shoot}
                                    disabled={processing || !stream}
                                    data-testid={`${testId}-shoot`}
                                >
                                    {processing ? (
                                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Camera className="mr-1 h-4 w-4" />
                                    )}
                                    Auslösen
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
